import { baseApi } from '@shared/api/baseApi';
import type { ApiMessage } from '@shared/api/response';
import type { MeWithPages, SessionResponse, TwoStepRequired } from '@shared/api/types';
import { announceSignedOut } from '@shared/session/authChannel';
import { profileReceived, sessionReceived, signedOut } from '@shared/session/sessionSlice';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface ChangePasswordRequest {
  currentPassword?: string | undefined;
  newPassword: string;
}

export function isSession(data: SessionResponse | TwoStepRequired): data is SessionResponse {
  return 'accessToken' in data;
}

const authApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    /** A session, or (two-step sign-in on) a challenge for `loginTwoStep`. */
    login: build.mutation<ApiMessage<SessionResponse | TwoStepRequired>, LoginRequest>({
      query: (body) => ({ url: '/auth/login', method: 'POST', body }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (!isSession(data.data)) return;
          dispatch(baseApi.util.resetApiState());
          dispatch(sessionReceived(data.data));
        } catch {
          /* the form shows the error from the mutation result */
        }
      },
    }),
    loginTwoStep: build.mutation<ApiMessage<SessionResponse>, { challenge: string; code: string }>({
      query: (body) => ({ url: '/auth/login/two-step', method: 'POST', body }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(baseApi.util.resetApiState());
          dispatch(sessionReceived(data.data));
        } catch {
          /* the form shows the error from the mutation result */
        }
      },
    }),
    refreshSession: build.mutation<ApiMessage<SessionResponse>, undefined>({
      query: () => ({ url: '/auth/refresh', method: 'POST' }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(sessionReceived(data.data));
        } catch {
          dispatch(signedOut());
        }
      },
    }),
    logout: build.mutation<ApiMessage, { everywhere?: boolean } | undefined>({
      query: (arg) => ({ url: arg?.everywhere ? '/auth/logout-all' : '/auth/logout', method: 'POST' }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        // Sign out locally no matter what the server says.
        await queryFulfilled.catch(() => undefined);
        dispatch(signedOut('user'));
        dispatch(baseApi.util.resetApiState());
        announceSignedOut();
      },
    }),
    me: build.query<MeWithPages, undefined>({
      query: () => '/auth/me',
      providesTags: ['Me'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(profileReceived(data));
        } catch {
          /* a failed /me leaves the current session as it is */
        }
      },
    }),
    forgotPassword: build.mutation<ApiMessage, { email: string }>({
      query: (body) => ({ url: '/auth/password/forgot', method: 'POST', body }),
    }),
    validateResetToken: build.query<{ valid: boolean; purpose?: string | null }, string>({
      query: (token) => ({ url: '/auth/password/reset/validate', params: { token } }),
    }),
    resetPassword: build.mutation<ApiMessage, { token: string; newPassword: string }>({
      query: (body) => ({ url: '/auth/password/reset', method: 'POST', body }),
    }),
    changePassword: build.mutation<ApiMessage<SessionResponse>, ChangePasswordRequest>({
      query: (body) => ({ url: '/auth/password/change', method: 'POST', body }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(sessionReceived(data.data));
        } catch {
          /* the form shows the error from the mutation result */
        }
      },
    }),
    googleEnabled: build.query<{ enabled: boolean }, undefined>({
      query: () => '/auth/google/enabled',
    }),
  }),
});

export const {
  useLoginMutation,
  useLoginTwoStepMutation,
  useRefreshSessionMutation,
  useLogoutMutation,
  useMeQuery,
  useForgotPasswordMutation,
  useValidateResetTokenQuery,
  useResetPasswordMutation,
  useChangePasswordMutation,
  useGoogleEnabledQuery,
} = authApi;
